<template>
  <div class="role-select-page">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- 主要内容 -->
    <div class="role-select-content">
      <!-- Logo 区域 -->
      <div class="logo-section">
        <div class="logo-icon">🎭</div>
        <h1 class="app-name">{{ $t('roleSelect.title') }}</h1>
        <p class="app-tagline">{{ $t('roleSelect.tagline') }}</p>
      </div>

      <!-- 角色选择卡片 -->
      <div class="role-cards">
        <div
          v-for="role in roleOptions"
          :key="role.roleId"
          class="role-card"
          :class="{ selected: selectedRole && selectedRole.roleId === role.roleId }"
          @click="confirmRoleSelection(role)"
        >
          <div class="role-icon">{{ role.icon }}</div>
          <h3 class="role-name">{{ getRoleName(role) }}</h3>
          <p class="role-desc">{{ getRoleDesc(role) }}</p>
          <div class="role-code">{{ role.roleCode }}</div>
        </div>
      </div>

      <!-- 登出按钮 -->
      <div class="logout-section">
        <button class="btn-logout" @click="logout">
          🚪 {{ $t('roleSelect.logout') }}
        </button>
      </div>

    </div>

    <!-- 选择供应商/档口弹窗 -->
    <div v-if="showEntitySelectModal" class="modal-overlay" @click="closeEntitySelect">
      <div class="entity-select-modal" @click.stop>
        <div class="modal-header">
          <div class="modal-title">{{ modalTitle }}</div>
          <button class="modal-close" @click="closeEntitySelect">×</button>
        </div>
        <div class="modal-body">
          <!-- 选择现有 -->
          <div class="form-group">
            <label>{{ $t('roleSelect.selectExisting') }}{{ entityTypeName }}</label>
            <select v-model="selectedEntityId" class="form-select">
              <option value="">{{ $t('roleSelect.pleaseSelect') }}</option>
              <option v-for="item in entityList" :key="item.id" :value="item.id">
                {{ item.companyName || item.customerName }}
              </option>
            </select>
          </div>
          
          <div class="divider">
            <span>{{ $t('roleSelect.or') }}</span>
          </div>
          
          <!-- 创建新实体 -->
          <div class="form-group">
            <label>{{ $t('roleSelect.createNew') }}{{ entityTypeName }}</label>
            <input 
              v-model="newEntity.name"
              :placeholder="$t('roleSelect.namePlaceholder')"
              class="form-input"
            />
            <input 
              v-model="newEntity.address"
              :placeholder="$t('roleSelect.addressPlaceholder')"
              class="form-input"
              style="margin-top: 8px;"
            />
            <input 
              v-model="newEntity.phone"
              :placeholder="$t('roleSelect.phonePlaceholder')"
              class="form-input"
              style="margin-top: 8px;"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="closeEntitySelect">{{ $t('roleSelect.cancel') }}</button>
          <button class="btn-confirm" @click="confirmEntitySelection">{{ $t('roleSelect.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- Toast 提示 -->
    <div v-if="toast.show" :class="['toast', `toast-${toast.type}`, { show: toast.show }]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import AppConfig from '@/config';

const api = axios.create({
  baseURL: AppConfig.backendUrl + '/api',
  timeout: 30000
});

export default {
  name: 'RoleSelect',
  data() {
    return {
      roleOptions: [],
      selectedRole: null,
      confirming: false,
      showEntitySelectModal: false,
      entityList: [],
      selectedEntityId: '',
      newEntity: {
        name: '',
        address: '',
        phone: ''
      },
      toast: {
        show: false,
        message: '',
        type: 'info'
      }
    };
  },
  async mounted() {
    await this.loadRoleOptions();
  },
  computed: {
    modalTitle() {
      return this.selectedRole?.roleCode === 'WHOLESALER' ? this.$t('roleSelect.linkWholesaler') : this.$t('roleSelect.linkStall');
    },
    entityTypeName() {
      return this.selectedRole?.roleCode === 'WHOLESALER' ? this.$t('roleSelect.wholesaler') : this.$t('roleSelect.stall');
    }
  },
  methods: {
    getRoleName(role) {
      const roleCode = role?.roleCode?.toUpperCase();
      const key = `roleSelect.roles.${roleCode}.name`;
      const fallback = role?.roleName || roleCode;
      // vue-i18n v8: 检查翻译是否存在
      if (this.$te(key)) {
        return this.$t(key);
      }
      return fallback;
    },
    getRoleDesc(role) {
      const roleCode = role?.roleCode?.toUpperCase();
      const key = `roleSelect.roles.${roleCode}.description`;
      const fallback = role?.description || this.$t('roleSelect.noDescription');
      if (this.$te(key)) {
        return this.$t(key);
      }
      return fallback;
    },
    async loadRoleOptions() {
      try {
        // 优先从 sessionStorage 获取已保存的角色选项（Apple/Google 登录场景）
        const savedRoleOptions = sessionStorage.getItem('roleOptions');
        if (savedRoleOptions) {
          this.roleOptions = JSON.parse(savedRoleOptions);
          console.log('✅ 从 sessionStorage 加载角色选项:', this.roleOptions);
          if (this.roleOptions.length === 0) {
            this.showToast(this.$t('roleSelect.roleNotFound'), 'error');
          }
          return;
        }

        // 从 sessionStorage 获取临时凭证
        const username = sessionStorage.getItem('tempUsername');
        const password = sessionStorage.getItem('tempPassword');

        if (!username || !password) {
          // 尝试用 tempToken 获取角色信息
          const token = sessionStorage.getItem('tempToken');
          if (token) {
            // 用 token 调用 /auth/me 获取角色
            try {
              const meResponse = await api.get('/auth/me', {
                headers: { 'Authorization': 'Bearer ' + token }
              });
              if (meResponse && meResponse.roleOptions) {
                this.roleOptions = meResponse.roleOptions;
                return;
              }
            } catch (e) {
              console.warn('用 token 获取角色失败:', e);
            }
          }
          this.showToast(this.$t('roleSelect.pleaseLoginFirst'), 'error');
          setTimeout(() => {
            this.$router.push('/login');
          }, 2000);
          return;
        }

        // 调用登录接口获取角色选项
        const response = await api.post('/auth/login', {
          username,
          password
        });

        if (response.data && response.data.roleOptions) {
          this.roleOptions = response.data.roleOptions;
        } else {
          this.showToast(this.$t('roleSelect.roleNotFound'), 'error');
        }
      } catch (error) {
        console.error('加载角色选项失败:', error);
        this.showToast(this.$t('roleSelect.loadFailed') + (error.response?.data?.message || error.message), 'error');
      }
    },

    async confirmRoleSelection(role) {
      if (!role) {
        this.showToast(this.$t('roleSelect.pleaseSelectRole'), 'error');
        return;
      }

      this.selectedRole = role;
      const roleCode = role.roleCode?.toUpperCase();
      
      // 如果是批发商角色，检查是否有商户
      if (roleCode === 'WHOLESALER') {
        // 从 sessionStorage 获取登录时返回的商户列表
        const wholesalerOptions = JSON.parse(sessionStorage.getItem('wholesalerOptions') || '[]');
        
        if (wholesalerOptions.length === 0) {
          // 没有关联商户，提示用户
          this.showToast(this.$t('roleSelect.noWholesalerLinked'), 'error');
          return;
        } else if (wholesalerOptions.length === 1) {
          // 只有一个商户，直接登录
          await this.completeLogin(wholesalerOptions[0].wholesalerId);
          return;
        } else {
          // 多个商户，显示选择框
          this.entityList = wholesalerOptions;
          this.modalTitle = this.$t('roleSelect.selectWholesaler');
          this.showEntitySelectModal = true;
          return;
        }
      }
      
      // 管理员角色，直接登录
      await this.completeLogin(null);
    },

    closeEntitySelect() {
      this.showEntitySelectModal = false;
      this.selectedEntityId = '';
      this.newEntity = { name: '', address: '', phone: '' };
    },

    async confirmEntitySelection() {
      const roleCode = this.selectedRole?.roleCode?.toUpperCase();
      
      if (!this.selectedEntityId && !this.newEntity.name) {
        this.showToast(this.$t('roleSelect.pleaseSelectOrCreate') + this.entityTypeName, 'error');
        return;
      }

      try {
        let entityId = this.selectedEntityId;
        
        // 如果创建新实体
        if (!entityId && this.newEntity.name) {
          const url = roleCode === 'WHOLESALER' 
            ? '/admin/wholesaler' 
            : '/admin/customer';
          
          const payload = roleCode === 'WHOLESALER'
            ? {
                companyName: this.newEntity.name,
                address: this.newEntity.address,
                contactPhone: this.newEntity.phone
              }
            : {
                customerName: this.newEntity.name,
                address: this.newEntity.address,
                contactPhone: this.newEntity.phone
              };
          
          const response = await api.post(url, payload);
          entityId = response.id;
        }
        
        // 完成登录并关联
        await this.completeLogin(entityId);
      } catch (error) {
        console.error('操作失败:', error);
        this.showToast(this.$t('roleSelect.operationFailed') + (error.response?.data?.message || error.message), 'error');
      }
    },

    async completeLogin(entityId) {
      if (!this.selectedRole) {
        this.showToast(this.$t('roleSelect.pleaseSelectRole'), 'error');
        return;
      }

      this.confirming = true;
      try {
        // 直接使用登录时返回的 token（从 sessionStorage 获取）
        const token = sessionStorage.getItem('tempToken');
        const wholesalerOptions = JSON.parse(sessionStorage.getItem('wholesalerOptions') || '[]');
        
        // 保存角色信息
        localStorage.setItem('user_identity', this.selectedRole.roleCode);
        // ⭐ 保存 user_id
        const tempUserId = sessionStorage.getItem('tempUserId');
        if (tempUserId) localStorage.setItem('user_id', tempUserId);
        
        // 如果是批发商或档口角色，保存对应的 ID
        if (this.selectedRole.roleCode === 'WHOLESALER') {
          if (entityId) {
            localStorage.setItem('stored_wholesaler_id', entityId.toString());
          } else if (wholesalerOptions.length === 1) {
            localStorage.setItem('stored_wholesaler_id', wholesalerOptions[0].wholesalerId.toString());
          }
        } else if (['STALL', 'CUSTOMER'].includes(this.selectedRole.roleCode)) {
          if (entityId) {
            localStorage.setItem('stored_customer_id', entityId.toString());
          } else {
            // ⭐ 用登录时返回的 customerId
            const cid = sessionStorage.getItem('customerId');
            if (cid) {
              localStorage.setItem('stored_customer_id', cid);
            }
          }
          // ⭐ 如果 URL 带了批发商ID，自动关联档口和批发商
          const urlWholesalerId = sessionStorage.getItem('url_wholesaler_id');
          if (urlWholesalerId) {
            try {
              await api.post('/auth/link-wholesaler', { wholesalerId: parseInt(urlWholesalerId) });
              console.log('✅ 档口自动关联批发商成功:', urlWholesalerId);
            } catch (e) {
              console.warn('⚠️ 自动关联批发商失败:', e);
            }
          }
        } else if (this.selectedRole.roleCode === 'DRIVER') {
          // 配送员角色：查询配送员信息并保存
          try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const phone = userInfo.username || userInfo.phone;
            if (phone) {
              const res = await api.get('/delivery/drivers/check-phone', {
                params: { phone }
              });
              if (res?.exists && res?.driverId) {
                localStorage.setItem('driver_id', res.driverId.toString());
                // 保存配送员基本信息
                const driverInfo = {
                  id: res.driverId,
                  name: userInfo.username || '配送员',
                  phone: phone,
                  status: 'IDLE'
                };
                localStorage.setItem('driver_info', JSON.stringify(driverInfo));
                console.log('✅ 保存配送员信息:', driverInfo);
              }
            }
          } catch (error) {
            console.error('❌ 获取配送员信息失败:', error);
          }
        }
        
        // 保存用户权限（从 sessionStorage 获取）
        const features = sessionStorage.getItem('userFeatures');
        if (features) {
          localStorage.setItem('user_features', features);
        }

        this.showToast(this.$t('roleSelect.loginSuccess'), 'success');

        // 清除临时数据
        sessionStorage.removeItem('tempUsername');
        sessionStorage.removeItem('tempPassword');
        sessionStorage.removeItem('tempToken');
        sessionStorage.removeItem('needsRoleSelect');
        sessionStorage.removeItem('wholesalerOptions');
        sessionStorage.removeItem('userFeatures');

        // 跳转到对应页面
        setTimeout(() => {
          this.closeEntitySelect();
          
          // 优先使用登录时保存的 redirect
          const savedRedirect = sessionStorage.getItem('login_redirect');
          if (savedRedirect) {
            sessionStorage.removeItem('login_redirect');
            console.log('🚀 跳转到保存的 redirect:', savedRedirect);
            this.$router.replace(savedRedirect);
            return;
          }
          
          // 根据角色设置正确的首页
          let redirectPath = this.selectedRole.redirectPath;
          if (this.selectedRole.roleCode === 'DRIVER') {
            redirectPath = '/delivery';  // 配送员首页
          }
          
          this.$router.replace(redirectPath);
        }, 500);
      } catch (error) {
        console.error('角色登录失败:', error);
        this.showToast(this.$t('roleSelect.loginFailed') + (error.response?.data?.message || error.message), 'error');
      } finally {
        this.confirming = false;
      }
    },

    goBack() {
      // 清除临时凭证
      sessionStorage.removeItem('tempUsername');
      sessionStorage.removeItem('tempPassword');
      sessionStorage.removeItem('needsIdentitySelect');
      this.$router.push('/login');
    },

    logout() {
      // 调用 Vuex store 的 logout 方法统一处理
      this.$store.dispatch('auth/logout');
      // 清除所有临时凭证
      sessionStorage.removeItem('tempUsername');
      sessionStorage.removeItem('tempPassword');
      sessionStorage.removeItem('tempToken');
      sessionStorage.removeItem('needsRoleSelect');
      sessionStorage.removeItem('wholesalerOptions');
      sessionStorage.removeItem('userFeatures');
      sessionStorage.removeItem('customerId');
      // 跳转回登录页
      this.$router.push('/login');
    },

    showToast(message, type = 'info') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      
      setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    }
  }
};
</script>

<style lang="scss" scoped>
.role-select-page {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

// 背景装饰
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  
  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    animation: float 20s infinite ease-in-out;
  }
  
  .circle-1 {
    width: 200px;
    height: 200px;
    top: -50px;
    left: -50px;
    animation-delay: 0s;
  }
  
  .circle-2 {
    width: 150px;
    height: 150px;
    bottom: 100px;
    right: -30px;
    animation-delay: -5s;
  }
  
  .circle-3 {
    width: 100px;
    height: 100px;
    top: 50%;
    right: 20%;
    animation-delay: -10s;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(180deg); }
}

// 主要内容
.role-select-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
}

// Logo 区域
.logo-section {
  text-align: center;
  margin-bottom: 40px;
  
  .logo-icon {
    font-size: 64px;
    margin-bottom: 16px;
    animation: pulse 2s infinite;
  }
  
  .app-name {
    font-size: 28px;
    font-weight: bold;
    color: white;
    margin: 0 0 8px 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  .app-tagline {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

// 角色卡片
.role-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.role-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  &.selected {
    border: 3px solid #667eea;
    background: #f9f0ff;
    transform: translateY(-4px);
  }
  
  .role-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }
  
  .role-name {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
  }
  
  .role-desc {
    font-size: 13px;
    color: #666;
    margin: 0 0 12px 0;
    min-height: 36px;
  }
  
  .role-code {
    font-size: 12px;
    color: #999;
    font-family: monospace;
    background: #f5f5f5;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
  }
}

// 确认按钮
.btn-confirm {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

// 登出按钮
.logout-section {
  margin-top: 20px;
}

.btn-logout {
  width: 100%;
  padding: 14px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

// 返回按钮
.btn-back {
  width: 100%;
  padding: 14px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
}

// Toast 提示
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100px);
  padding: 14px 24px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  z-index: 1000;
  opacity: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  max-width: 90%;
  text-align: center;
  
  &.show {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
  
  &.toast-success { background: #2ecc71; }
  &.toast-error { background: #e74c3c; }
  &.toast-info { background: #3498db; }
}

// 实体选择弹窗
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.entity-select-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-select,
.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.divider {
  text-align: center;
  margin: 20px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: #eee;
  }
  
  span {
    position: relative;
    background: white;
    padding: 0 16px;
    color: #999;
    font-size: 13px;
  }
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.btn-cancel:hover {
  background: #e0e0e0;
}

.btn-confirm {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}
</style>
